import { useMemo, useState } from "react";
import { ArrowLeft, ChevronDown, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useAuth } from "@/hooks/useAuth";
import type {
  AIGenerationConversation,
  PhaseWorkoutPreviewPayload,
} from "@/integrations/backend/program-generation";
import { parsePhaseWorkoutsAssistantContent } from "@/integrations/backend/program-generation";
import {
  generationFailureDetail,
  isAssistantGenerationFailure,
  latestAssistantMessage,
  parseCuratedWorkoutPreview,
} from "@/integrations/backend/workout-generator";
import AIGenerationDebugSidebar from "../AIGenerationDebugSidebar";

type WorkoutPreviewProps = {
  workouts: NonNullable<ReturnType<typeof parseCuratedWorkoutPreview>>["workouts"];
  exerciseNameMap?: Record<string, string>;
};

function exerciseCountForWorkout(workout: PhaseWorkoutPreviewPayload["workouts"][number]): number {
  return workout.groupings.reduce((sum, g) => sum + (g.exercises?.length ?? 0), 0);
}

function workoutDateRange(workouts: PhaseWorkoutPreviewPayload["workouts"]): string | null {
  const dates = workouts.map((w) => w.date).filter(Boolean).sort();
  if (dates.length === 0) return null;
  if (dates.length === 1) return dates[0];
  return `${dates[0]} – ${dates[dates.length - 1]}`;
}

function WorkoutChatSummary({ workouts }: { workouts: PhaseWorkoutPreviewPayload["workouts"] }) {
  const range = workoutDateRange(workouts);
  const totalExercises = workouts.reduce((sum, w) => sum + exerciseCountForWorkout(w), 0);

  return (
    <div className="space-y-2 text-sm leading-relaxed text-gray-800">
      <p>
        Generated <span className="font-medium text-gray-900">{workouts.length}</span> workout
        {workouts.length === 1 ? "" : "s"}
        {range ? (
          <>
            {" "}
            (<span className="text-gray-700">{range}</span>)
          </>
        ) : null}
        {" "}
        with <span className="font-medium text-gray-900">{totalExercises}</span> exercise
        {totalExercises === 1 ? "" : "s"} total. Use the preview panel for full structure and prescriptions.
      </p>
      <ul className="space-y-1 border-t border-gray-100 pt-2">
        {workouts.map((workout, workoutIdx) => (
          <li key={`${workout.name}-${workoutIdx}`} className="text-xs text-gray-600">
            <span className="font-medium text-gray-800">{workout.name}</span>
            <span className="text-gray-400"> · </span>
            {workout.date}
            <span className="text-gray-400"> · </span>
            {workout.duration_minutes} min
            <span className="text-gray-400"> · </span>
            {exerciseCountForWorkout(workout)} exercises
          </li>
        ))}
      </ul>
    </div>
  );
}

function RawResponseCollapsible({ content }: { content: string }) {
  const [open, setOpen] = useState(false);
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mt-2 border-t border-gray-100 pt-2">
      <CollapsibleTrigger className="flex w-full items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700">
        <ChevronDown
          className={`h-3.5 w-3.5 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden
        />
        View raw response
      </CollapsibleTrigger>
      <CollapsibleContent>
        <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap rounded border border-gray-200 bg-gray-50 p-2 font-mono text-[11px] text-gray-700">
          {content}
        </pre>
      </CollapsibleContent>
    </Collapsible>
  );
}

function ChatMessageBody({
  msg,
  isLatestAssistant,
  latestPreview,
  showRawResponse,
}: {
  msg: { role: string; content: string };
  isLatestAssistant: boolean;
  latestPreview: PhaseWorkoutPreviewPayload | null;
  showRawResponse: boolean;
}) {
  if (msg.role === "user") {
    return <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>;
  }

  if (isAssistantGenerationFailure(msg.content)) {
    return (
      <p className="whitespace-pre-wrap text-red-700">{generationFailureDetail(msg.content)}</p>
    );
  }

  const parsed =
    isLatestAssistant && latestPreview?.workouts?.length
      ? latestPreview
      : parsePhaseWorkoutsAssistantContent(msg.content);

  if (parsed?.workouts?.length) {
    return (
      <>
        <WorkoutChatSummary workouts={parsed.workouts} />
        {showRawResponse ? <RawResponseCollapsible content={msg.content} /> : null}
      </>
    );
  }

  const trimmed = (msg.content || "").trim();
  if (!trimmed) {
    return <p className="text-gray-500">Empty response.</p>;
  }

  return (
    <>
      <p className="text-gray-600">Response could not be parsed as workouts.</p>
      {showRawResponse ? <RawResponseCollapsible content={msg.content} /> : null}
    </>
  );
}

function WorkoutPreviewList({ workouts, exerciseNameMap }: WorkoutPreviewProps) {
  const displayName = (ex: { exercise_id: string; exercise_name?: string | null }) =>
    ex.exercise_name ?? exerciseNameMap?.[ex.exercise_id] ?? ex.exercise_id;

  return (
    <div className="space-y-3 text-sm">
      {workouts.map((workout, workoutIdx) => (
        <div key={`${workout.name}-${workoutIdx}`} className="rounded-lg border border-gray-200 bg-white p-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-gray-900">{workout.name}</span>
            <span className="text-gray-400">•</span>
            <span className="text-gray-600">{workout.date}</span>
            <span className="text-gray-400">•</span>
            <span className="text-gray-600">{workout.duration_minutes} min</span>
          </div>
          {workout.description ? (
            <p className="mt-1 whitespace-pre-wrap text-xs text-gray-600">{workout.description}</p>
          ) : null}
          <div className="mt-2 space-y-2">
            {workout.groupings.map((grouping, groupingIdx) => (
              <div key={groupingIdx} className="rounded border border-gray-200 bg-gray-50 p-2">
                <div className="text-xs font-medium text-gray-700">
                  Group {groupingIdx + 1} ({grouping.exercises.length} exercises)
                  {grouping.sets != null ? ` — Sets ${grouping.sets}` : ""}
                  {grouping.rest_seconds != null ? `, Rest ${grouping.rest_seconds}s` : ""}
                </div>
                <ul className="mt-1 list-inside list-disc text-xs text-gray-600">
                  {grouping.exercises.map((exercise, exerciseIdx) => (
                    <li key={`${exercise.exercise_id}-${exerciseIdx}`}>
                      {displayName(exercise)}
                      {exercise.reps != null ? ` — ${exercise.reps} reps` : ""}
                      {exercise.time_seconds != null ? ` — ${exercise.time_seconds}s` : ""}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

type Props = {
  conversation: AIGenerationConversation | null;
  input: string;
  onInputChange: (value: string) => void;
  busy: boolean;
  onSend: () => void;
  onRestart: () => void;
  onConfirm: () => void;
  onBack: () => void;
  exerciseNameMap: Record<string, string>;
};

export default function CuratedWorkoutChatPanel({
  conversation,
  input,
  onInputChange,
  busy,
  onSend,
  onRestart,
  onConfirm,
  onBack,
  exerciseNameMap,
}: Props) {
  const { profile } = useAuth();
  const showDebug = Boolean(profile?.debugger_enabled);

  const messages = conversation?.messages ?? [];
  const lastAssistant = useMemo(() => latestAssistantMessage(conversation), [conversation]);
  const lastAssistantIndex = useMemo(() => {
    let idx = -1;
    messages.forEach((m, i) => {
      if (m.role === "assistant") idx = i;
    });
    return idx;
  }, [messages]);

  const generationFailed = useMemo(
    () => isAssistantGenerationFailure(lastAssistant?.content),
    [lastAssistant?.content]
  );
  const generationFailureMessage = useMemo(
    () => (generationFailed ? generationFailureDetail(lastAssistant?.content) : null),
    [generationFailed, lastAssistant?.content]
  );

  const preview = useMemo(() => parseCuratedWorkoutPreview(conversation), [conversation]);

  const previewError = useMemo(() => {
    if (generationFailureMessage) return generationFailureMessage;
    const err = conversation?.parse_error;
    if (!err || err === "assistant output is not valid JSON") {
      return generationFailed ? generationFailureMessage : err ?? null;
    }
    return err;
  }, [conversation?.parse_error, generationFailed, generationFailureMessage]);

  const canConfirm =
    !busy &&
    !generationFailed &&
    Boolean(preview?.workouts?.length) &&
    !previewError;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-9 gap-1.5 px-2 text-sm font-medium text-gray-800 hover:bg-gray-100 hover:text-gray-950"
          onClick={onBack}
        >
          <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
          Back to exercise review
        </Button>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" disabled={busy} onClick={onRestart}>
            Clear and start over
          </Button>
          <Button type="button" size="sm" disabled={!canConfirm} onClick={onConfirm}>
            Save workouts
          </Button>
        </div>
      </div>

      <div
        className={`grid gap-4 ${showDebug ? "lg:grid-cols-3" : "lg:grid-cols-2"}`}
      >
        <div
          className={`max-h-[60vh] space-y-3 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-4 ${
            showDebug ? "lg:col-span-1" : ""
          }`}
        >
          {messages.length === 0 && !busy ? (
            <p className="text-sm text-gray-500">Workouts will appear here after generation.</p>
          ) : null}
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`rounded-lg p-3 text-sm ${
                msg.role === "user" ? "ml-8 bg-primary/10 text-gray-900" : "mr-8 bg-white text-gray-800"
              }`}
            >
              <div className="mb-1 text-xs font-medium uppercase text-gray-500">
                {msg.role === "user" ? "You" : "Assistant"}
              </div>
              <ChatMessageBody
                msg={msg}
                isLatestAssistant={msg.role === "assistant" && idx === lastAssistantIndex}
                latestPreview={preview}
                showRawResponse={showDebug}
              />
            </div>
          ))}
          {busy ? (
            <div className="mr-8 rounded-lg bg-white p-3 text-sm text-gray-500">
              <div className="mb-1 text-xs font-medium uppercase text-gray-500">Assistant</div>
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Generating…
              </div>
            </div>
          ) : null}
        </div>

        <div
          className={`max-h-[60vh] overflow-y-auto rounded-lg border border-gray-200 bg-white p-4 ${
            showDebug ? "lg:col-span-1" : ""
          }`}
        >
          <h3 className="mb-3 font-semibold text-gray-900">Workout preview</h3>
          {generationFailed ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-3">
              <p className="text-sm font-medium text-red-800">Generation failed</p>
              <p className="mt-1 whitespace-pre-wrap text-sm text-red-700">{generationFailureMessage}</p>
            </div>
          ) : previewError ? (
            <p className="text-sm text-red-600">{previewError}</p>
          ) : preview?.workouts?.length ? (
            <WorkoutPreviewList workouts={preview.workouts} exerciseNameMap={exerciseNameMap} />
          ) : (
            <p className="text-sm text-gray-500">Preview will appear after the assistant responds.</p>
          )}
        </div>

        {showDebug ? (
          <AIGenerationDebugSidebar
            conversation={conversation}
            busy={busy}
            className="lg:col-span-1 rounded-lg"
          />
        ) : null}
      </div>

      <div className="flex gap-2">
        <Textarea
          value={input}
          onChange={(e) => onInputChange(e.target.value)}
          placeholder="Ask for changes to sets, reps, exercise order…"
          rows={2}
          className="flex-1"
        />
        <Button type="button" disabled={busy || !input.trim()} onClick={onSend}>
          Send
        </Button>
      </div>
    </div>
  );
}
